import { useMemo } from 'react';
import { useCustomerDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { usePairingInventoryParams } from '@hungryroot/inventory';
import {
  usePairings,
  type CustomerPairingsParamsV3,
  type PairingListResultV3,
} from '@hungryroot/rest';
import { useCustomerPairingsV3 } from '@hungryroot/rest-hooks';

/**
 * Tag IDs of the Chef's Kitchen sub-categories. Must stay in sync with
 * `chefsKitchenFilter` in `packages/app-public/src/components/Filters/RecipeFiltersV2.tsx`.
 */
const CHEFS_KITCHEN_TAG_IDS = [
  4225, 4226, 4611, 4612, 4613, 4771, 4772, 4773, 4774, 4974, 5517, 5527, 5528,
  5529, 5530,
] as const;

export const TOP_CHEFS_KITCHEN_DISPLAY_COUNT = 8;

// Over-fetch candidates so that, after dropping recipes without a featured
// image, we still have enough to fill TOP_CHEFS_KITCHEN_DISPLAY_COUNT slots.
const CANDIDATE_LIMIT = 48;

type DietaryRestrictionFlag = keyof Pick<
  CustomerPairingsParamsV3,
  | 'is_gluten_free'
  | 'is_dairy_free'
  | 'is_vegan'
  | 'is_vegetarian'
  | 'is_pescatarian'
  | 'is_nut_free'
  | 'is_peanut_free'
  | 'is_egg_free'
  | 'is_soy_free'
  | 'is_shellfish_free'
  | 'is_sesame_free'
  | 'is_garlic_free'
  | 'is_onion_free'
  | 'is_grain_free'
  | 'is_caffeine_free'
>;

const DIETARY_RESTRICTION_FLAGS: ReadonlySet<DietaryRestrictionFlag> = new Set([
  'is_gluten_free',
  'is_dairy_free',
  'is_vegan',
  'is_vegetarian',
  'is_pescatarian',
  'is_nut_free',
  'is_peanut_free',
  'is_egg_free',
  'is_soy_free',
  'is_shellfish_free',
  'is_sesame_free',
  'is_garlic_free',
  'is_onion_free',
  'is_grain_free',
  'is_caffeine_free',
]);

function isDietaryRestrictionFlag(
  fieldName: string
): fieldName is DietaryRestrictionFlag {
  return DIETARY_RESTRICTION_FLAGS.has(fieldName as DietaryRestrictionFlag);
}

/** Fields from the pairings response that this view actually consumes. */
export type PairingForDisplay = Omit<PairingListResultV3, 'tags'>;

export interface UseTopChefsKitchenRecipesResult {
  /** Up to TOP_CHEFS_KITCHEN_DISPLAY_COUNT recipes sorted by points desc. */
  recipes: PairingForDisplay[];
  /** Total Chef's Kitchen recipe count matching the customer's diet. */
  count: number;
  isLoading: boolean;
  isError: boolean;
}

function pointsOf(pairing: PairingForDisplay): number {
  if (pairing.points === null) return 0;
  const parsed = parseFloat(pairing.points);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Fetches the top Chef's Kitchen recipes for the customer's diet, sorted by
 * `points` descending. Two-step: customer-pairings narrows by Chef's Kitchen
 * tags + dietary flags + inventory and gives us the total `count`; then
 * `usePairings` hydrates so we have `points`, `featured_img_url`, etc. to
 * render and sort.
 */
export function useTopChefsKitchenRecipes(): UseTopChefsKitchenRecipesResult {
  const inventoryParams = usePairingInventoryParams();

  const { data: dietaryRestrictions, isLoading: isDietaryRestrictionsLoading } =
    useCustomerDietaryRestrictions();

  const dietaryFlags = useMemo<
    Partial<Record<DietaryRestrictionFlag, true>>
  >(() => {
    const flags: Partial<Record<DietaryRestrictionFlag, true>> = {};
    for (const restriction of dietaryRestrictions?.dietary_restrictions ?? []) {
      if (isDietaryRestrictionFlag(restriction.field_name)) {
        flags[restriction.field_name] = true;
      }
    }
    return flags;
  }, [dietaryRestrictions?.dietary_restrictions]);

  const {
    data: candidatePairingsData,
    isLoading: isCandidateLoading,
    isError: isCandidateError,
  } = useCustomerPairingsV3({
    queryParams: {
      chefs_kitchen_tags: [...CHEFS_KITCHEN_TAG_IDS],
      has_inventory: true,
      hide_dislike: true,
      exclude_nevers: true,
      ordering: '-points',
      limit: CANDIDATE_LIMIT,
      ...dietaryFlags,
      ...(inventoryParams ?? {}),
    },
    options: {
      enabled: !isDietaryRestrictionsLoading && inventoryParams !== undefined,
    },
  });

  const candidateIds = useMemo(
    () => (candidatePairingsData?.results ?? []).map((item) => item.id),
    [candidatePairingsData?.results]
  );

  const {
    data: pairingsData,
    isLoading: isPairingsLoading,
    isError: isPairingsError,
  } = usePairings({
    ids: candidateIds,
    limit: candidateIds.length,
    options: {
      enabled: candidateIds.length > 0,
    },
  });

  const recipes = useMemo(() => {
    const results = pairingsData?.results ?? [];
    // Only show recipes with a featured image; the card has no fallback for a
    // missing image. Over-fetching candidates (CANDIDATE_LIMIT) keeps enough
    // after this filter to fill TOP_CHEFS_KITCHEN_DISPLAY_COUNT slots.
    // Re-sort client-side so the display order is guaranteed even if
    // `usePairings` returns items in a different sequence than the
    // candidate IDs.
    return results
      .filter((recipe) => Boolean(recipe.featured_img_url))
      .sort((a, b) => pointsOf(b) - pointsOf(a))
      .slice(0, TOP_CHEFS_KITCHEN_DISPLAY_COUNT);
  }, [pairingsData?.results]);

  const isLoading =
    isDietaryRestrictionsLoading ||
    isCandidateLoading ||
    (candidateIds.length > 0 && isPairingsLoading);

  const isError = isCandidateError || isPairingsError;

  return {
    recipes,
    count: candidatePairingsData?.count ?? 0,
    isLoading,
    isError,
  };
}
